// Function-graph / trig keypoint label helpers (pure geometry + formatting).
//
// MOVED here verbatim from InteractiveGraph.js (module-level pure functions;
// the only change is the `export ` keyword on each moved top-level declaration)
// as maintenance-only Slice 1 of the InteractiveGraph split — same pattern as
// overlayStageVisibility.js (v2.14.19). InteractiveGraph imports them back, so
// renderer call sites are unchanged. FUNC_LABEL_CHAR_W stays module-private
// (its only consumer is placeFunctionLabels). Behavior must stay
// byte-identical with the pre-move code (guarded by e2e
// bidirectional-linkage/func-label-density.spec.js).

// Smart label offsets: above for peaks, below for valleys and on-axis points
export const computeKeypointLabelOffsets = (pt) => {
  if (pt.y > 0.01)  return { coordY: -18, nameY: -34 };   // above peaks
  if (pt.y < -0.01) return { coordY: 25, nameY: 42 };     // below valleys
  return { coordY: 38, nameY: 55 };                        // below on-axis (clears tick labels)
};

// Label offsets for non-trig function keypoints (slightly tighter than trig)
export const computeFuncLabelOffsets = (pt) => {
  if (pt.y > 0.01)  return { coordY: -14, nameY: -28 };   // above point
  if (pt.y < -0.01) return { coordY: 22, nameY: 38 };     // below point
  return { coordY: 30, nameY: 46 };                        // on-axis: below, clears tick labels
};

// Greedy no-overlap placement for function-graph coordinate labels.
//
// Screen-space collision using each label's x-anchor + an ESTIMATED text box —
// deliberately not getBBox(), which reads transient geometry while the curve's
// enter transition is still animating (the P2 "bbox 安定化" known-issue). Keypoint
// labels (the always-kept max/min/intercept/origin set) are placed FIRST and are
// never dropped — a colliding keypoint is re-stacked to an alternate row and
// flagged for a leader line. Sampled labels are placed only if they still fit, so
// the curve stays readable no matter how dense the sampling is.
//
// candidates: [{ x, y, text, offsetY, keypoint }]  (x/y already in screen space)
// returns:    [{ x, y, text, keypoint, leader, anchorX, anchorY }]  (y = final label y)
const FUNC_LABEL_CHAR_W = 0.62; // em per char — conservative for UD/gothic numeric glyphs
export const placeFunctionLabels = (candidates, { fontSize = 20, yMin = -Infinity, yMax = Infinity } = {}) => {
  const rowH = fontSize * 1.15;
  const halfH = fontSize * 0.6;
  const clampY = (y) => Math.max(yMin, Math.min(yMax, y));
  const boxOf = (cx, cy, text) => {
    const w = Math.max(String(text).length * fontSize * FUNC_LABEL_CHAR_W, fontSize);
    return { x1: cx - w / 2, x2: cx + w / 2, y1: cy - halfH, y2: cy + halfH };
  };
  const hit = (a, b) => a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1;
  const placed = [];
  const out = [];
  const emit = (c, labelY) => {
    placed.push(boxOf(c.x, labelY, c.text));
    // leader = the label ended up meaningfully offset from its natural position, so
    // the renderer draws a connector back to the point (avoids a detached number).
    out.push({
      x: c.x, y: labelY, text: c.text, keypoint: !!c.keypoint,
      leader: Math.abs(labelY - (c.y + (c.offsetY || 0))) > rowH * 0.5,
      anchorX: c.x, anchorY: c.y,
    });
  };
  const tryPlace = (c, mustPlace) => {
    const baseY = c.y + (c.offsetY || 0);
    const rows = mustPlace ? [0, -1, 1, -2, 2, -3, 3] : [0];
    for (let r = 0; r < rows.length; r++) {
      const labelY = clampY(baseY + rows[r] * rowH);
      if (!placed.some((p) => hit(boxOf(c.x, labelY, c.text), p))) {
        emit(c, labelY);
        return true;
      }
    }
    // Keypoints must NEVER be dropped: if every candidate row collides, place at the
    // (clamped) base row anyway — a slightly-overlapping keypoint beats a missing one.
    if (mustPlace) { emit(c, clampY(baseY)); return true; }
    return false;
  };
  candidates.filter((c) => c.keypoint).forEach((c) => tryPlace(c, true));   // keypoints: must place
  candidates.filter((c) => !c.keypoint).forEach((c) => tryPlace(c, false)); // sampled: fit or drop
  return out;
};

// Compute special keypoints for non-trig functions — the mathematically-defined
// members of the always-keep set: max, min, x-intercept(s), y-intercept, origin.
// Never fabricates a point the curve does not have: an asymptotic 1/x has no
// y-intercept and no origin point; a monotonic curve has no interior extremum, so
// its max/min are the visible-domain endpoints, labelled as such (visible max/min).
export const computeFuncKeypoints = (curvePoints, graphType) => {
  if (!curvePoints || curvePoints.length < 2) return [];
  const sorted = [...curvePoints].sort((a, b) => a.x - b.x);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const xSpan = Math.abs(last.x - first.x) || 1;
  const edgeTol = xSpan * 0.04; // "at the visible domain edge" tolerance
  const atEdge = (pt) => Math.abs(pt.x - first.x) <= edgeTol || Math.abs(pt.x - last.x) <= edgeTol;
  const kps = [];

  // Max / min. Over a finite sampled domain these always exist. An interior
  // extremum is a true max/min (最高点/最低点); an edge extremum is only a
  // visible-domain max/min (P2: a monotonic curve has no global extremum).
  // Added FIRST so their labels win the ε-dedup over any coincident start/end/vertex.
  const maxPt = sorted.reduce((a, b) => (b.y > a.y ? b : a));
  const minPt = sorted.reduce((a, b) => (b.y < a.y ? b : a));
  // Asymptote detection (shared): a segment whose local |slope| dwarfs the curve's
  // overall |slope| is an asymptotic jump (e.g. across 1/x's gap), not a real feature.
  // Used to (a) reject fabricated x/y crossings and (b) treat a near-asymptote peak
  // as a visible edge rather than a true extremum. (P2: never fabricate.)
  const overallSlope = Math.abs((maxPt.y - minPt.y) / xSpan) || 1;
  // 反比例 (y=k/x) is asymptotic: it never touches either axis or the origin. Gate its
  // intercepts/origin out entirely — the reliable defense when the curve is coarsely
  // sampled near the asymptote (where a slope test alone can be fooled). Shifted exp/
  // log are NOT gated (eˣ−2 has a real root), so only inverse_proportion is excluded.
  const isInverseProp = graphType === 'inverse_proportion';
  const isAsymptoticJump = (p, q) =>
    !!p && !!q && p.x !== q.x && Math.abs((q.y - p.y) / (q.x - p.x)) > 8 * overallSlope;
  const nearAsymptote = (pt) => {
    const idx = sorted.indexOf(pt);
    return isAsymptoticJump(sorted[idx - 1], pt) || isAsymptoticJump(pt, sorted[idx + 1]);
  };
  const isVisibleEdge = (pt) => atEdge(pt) || nearAsymptote(pt);
  if (maxPt !== minPt) {
    kps.push(isVisibleEdge(maxPt)
      ? { x: +maxPt.x.toFixed(2), y: +maxPt.y.toFixed(2), name: '可視域内 最大', nameKey: 'visibleMax' }
      : { x: +maxPt.x.toFixed(2), y: +maxPt.y.toFixed(2), name: '最高点', nameKey: 'maxPoint' });
    kps.push(isVisibleEdge(minPt)
      ? { x: +minPt.x.toFixed(2), y: +minPt.y.toFixed(2), name: '可視域内 最小', nameKey: 'visibleMin' }
      : { x: +minPt.x.toFixed(2), y: +minPt.y.toFixed(2), name: '最低点', nameKey: 'minPoint' });
  }

  // Raw start/end endpoints are deliberately NOT in the always-keep set — only the
  // required categories (max, min, x/y-intercept, origin) are. An endpoint that IS the
  // visible max/min is already captured above (isVisibleEdge → visibleMax/Min); a
  // non-extremal endpoint stays an ordinary sampled label (budget-controlled), so it
  // no longer shows as a fixed keypoint when the density is turned down.

  // x-axis crossings: detect y sign changes and interpolate. Skip a sign flip that
  // is really an asymptotic jump (1/x flips -∞→+∞ across its gap — not a real root).
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (!isInverseProp && prev.y * curr.y < 0 && !isAsymptoticJump(prev, curr)) {
      const t = prev.y / (prev.y - curr.y);
      const crossX = +(prev.x + t * (curr.x - prev.x)).toFixed(2);
      kps.push({ x: crossX, y: 0, name: 'x軸交点', nameKey: 'xIntercept' });
    }
  }

  // y-axis crossing: ONLY when the curve is continuous through x=0 (x=0 inside the
  // sampled domain and the bracketing segment is not an asymptotic jump). Guards
  // against inventing a y-intercept for 1/x (P2: do not fabricate).
  if (!isInverseProp && first.x <= 0 && last.x >= 0) {
    let L = null;
    let R = null;
    for (const p of sorted) {
      if (p.x <= 0 && (!L || p.x > L.x)) L = p;
      if (p.x >= 0 && (!R || p.x < R.x)) R = p;
    }
    if (L && R) {
      if (R.x === L.x) {
        kps.push({ x: 0, y: +L.y.toFixed(2), name: 'y軸交点', nameKey: 'yIntercept' });
      } else {
        const yAt0 = L.y + ((0 - L.x) / (R.x - L.x)) * (R.y - L.y);
        // Continuous crossing only — reject an asymptotic gap (1/x has no y-intercept). (P2)
        if (Number.isFinite(yAt0) && !isAsymptoticJump(L, R)) {
          kps.push({ x: 0, y: +yAt0.toFixed(2), name: 'y軸交点', nameKey: 'yIntercept' });
        }
      }
    }
  }

  // Quadratic vertex: interior extremum (kept as an explicit '頂点'; dedups away
  // when it coincides with the max/min point above, which is the usual case).
  if (graphType === 'quadratic_function') {
    const vMin = sorted.reduce((a, b) => (a.y < b.y ? a : b));
    const vMax = sorted.reduce((a, b) => (a.y > b.y ? a : b));
    const interior = (pt) => Math.abs(pt.x - first.x) > 0.5 && Math.abs(pt.x - last.x) > 0.5;
    if (interior(vMin)) kps.push({ x: +vMin.x.toFixed(2), y: +vMin.y.toFixed(2), name: '頂点', nameKey: 'vertex' });
    else if (interior(vMax)) kps.push({ x: +vMax.x.toFixed(2), y: +vMax.y.toFixed(2), name: '頂点', nameKey: 'vertex' });
  }

  // Relabel a keypoint as '原点' ONLY when it genuinely rounds to (0,0) at display
  // precision (1 decimal → |v| < 0.05), and NEVER overwrite its coordinates. This
  // stops a nearby point (e.g. a vertex at (0,-0.1) or a y-intercept at (0,0.1))
  // from being snapped onto an origin the curve does not actually pass through (P2).
  kps.forEach((kp) => {
    if (Math.abs(kp.x) < 0.05 && Math.abs(kp.y) < 0.05) {
      kp.name = '原点';
      kp.nameKey = 'origin';
    }
  });

  // Deduplicate: merge points within ε=0.3 (first occurrence wins)
  const deduped = [];
  kps.forEach(kp => {
    if (!deduped.find(d => Math.abs(d.x - kp.x) < 0.3 && Math.abs(d.y - kp.y) < 0.3)) {
      deduped.push(kp);
    }
  });
  return deduped;
};

export const formatRadianLabel = (x) => {
  if (Math.abs(x) < 1e-9) return '0';
  const pm = x / Math.PI;
  if (Math.abs(pm - Math.round(pm)) < 1e-6) {
    const n = Math.round(pm);
    if (n === 1) return '\u03C0';
    if (n === -1) return '-\u03C0';
    return `${n}\u03C0`;
  }
  for (const d of [2, 3, 4, 6]) {
    const num = Math.round(x * d / Math.PI);
    if (Math.abs(x - num * Math.PI / d) < 1e-6) {
      if (num === 1) return `\u03C0/${d}`;
      if (num === -1) return `-\u03C0/${d}`;
      return `${num}\u03C0/${d}`;
    }
  }
  return x.toFixed(2);
};

export const FUNCTION_GRAPH_TYPE_SET = new Set([
  'trigonometric_function', 'linear_function', 'quadratic_function',
  'exponential_function', 'logarithmic_function', 'inverse_proportion', 'proportion'
]);

// The coordinate-label density control governs the EXTRA sampled labels, which only
// the non-trig function renderers draw; a trig graph shows only keypoint coords, so
// the control is hidden there (it would otherwise appear but have no effect).
export const isNonTrigFunctionType = (graphType) => {
  const gt = String(graphType || '').toLowerCase();
  return FUNCTION_GRAPH_TYPE_SET.has(gt) && gt !== 'trigonometric_function';
};
